"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Building2, FileText, Magnet, Funnel, MessageSquare, ArrowRight, Cpu } from "lucide-react";
const growthModules = [{
  id: "mod-1",
  title: "Optical Fiber Networks",
  description: "High-density G657A2 solutions for zero-latency AI data center connectivity.",
  Icon: FileText
}, {
  id: "mod-2",
  title: "Drone Propulsion",
  description: "High-efficiency motors and propellers engineered for industrial-grade stability.",
  Icon: Magnet
}, {
  id: "mod-3",
  title: "Signal Transmission",
  description: "Advanced video transmission systems and antennas for long-range FPV operations.",
  Icon: Funnel
}, {
  id: "mod-4",
  title: "Technical Ecosystem",
  description: "Comprehensive B2B support and modular deployment for global integrators.",
  Icon: MessageSquare
}];
const getGridPlacement = (index: number) => {
  switch (index) {
    case 0:
      return "md:col-start-1 md:row-start-1";
    case 1:
      return "md:col-start-3 md:row-start-1";
    case 2:
      return "md:col-start-1 md:row-start-3";
    case 3:
      return "md:col-start-3 md:row-start-3";
    default:
      return "";
  }
};
export default function ScalableInfrastructure() {
  const headerReveal = useRevealOnScroll({
    threshold: 0.1
  });
  const diagramReveal = useRevealOnScroll({
    threshold: 0.2
  });
  return <section data-section-id="143937" id="strategic-applications" className="relative overflow-hidden bg-background py-24 lg:py-32 z-0 dark">
      {}
      <div className="absolute inset-0 -z-10 bg-[radial-gradient(var(--color-primary)_1px,transparent_1px)] [background-size:32px_32px] opacity-10"></div>
      
      {}
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-background via-background/80 to-background"></div>

      <div className="container mx-auto px-4 md:px-6">
        
        {}
        <div ref={headerReveal.ref} className={`mx-auto max-w-3xl text-center mb-16 transition-all duration-1000 ${headerReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/20 text-primary text-xs font-bold uppercase tracking-widest mb-6 border border-primary/30">
            <Cpu className="size-4" />
            <span>Industrial Innovation</span>
          </div>
          <h2 className="text-4xl md:text-6xl font-bold tracking-tighter text-foreground mb-6">
            <span className="text-primary">Strategic Solutions</span> for Next-Gen Infrastructure
          </h2>
          <p className="text-xl text-muted-foreground font-sans">
            Nexel Technologies integrates high-speed optical connectivity with advanced aerial systems to power the digital frontier and industrial automation.
          </p>
        </div>

        {}
        <div ref={diagramReveal.ref} className={`relative max-w-5xl mx-auto transition-all duration-1000 delay-200 ${diagramReveal.isVisible ? "opacity-100 scale-100" : "opacity-0 scale-95"}`}>
          {}
          <svg className="absolute inset-0 w-full h-full -z-10 hidden md:block text-primary/20" preserveAspectRatio="none" aria-hidden="true">
            {}
            <line x1="16.6%" y1="16.6%" x2="50%" y2="50%" stroke="currentColor" strokeWidth="1" strokeDasharray="8 4" />
            {}
            <line x1="83.3%" y1="16.6%" x2="50%" y2="50%" stroke="currentColor" strokeWidth="1" strokeDasharray="8 4" />
            {}
            <line x1="16.6%" y1="83.3%" x2="50%" y2="50%" stroke="currentColor" strokeWidth="1" strokeDasharray="8 4" />
            {}
            <line x1="83.3%" y1="83.3%" x2="50%" y2="50%" stroke="currentColor" strokeWidth="1" strokeDasharray="8 4" />
          </svg>

          {}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 items-center">
            
            {}
            <div className="md:col-start-2 md:row-start-2 flex justify-center py-8 md:py-0 relative z-10 order-first md:order-none">
              {}
              <div className="absolute inset-0 bg-primary/10 blur-3xl rounded-full scale-150 -z-10"></div>
              
              <div className="bg-foreground text-background p-8 rounded-xl shadow-2xl ring-1 ring-primary/50 flex flex-col items-center text-center w-full max-w-[280px] transform transition-transform hover:scale-105 duration-500">
                <div className="bg-background/20 p-4 rounded-lg mb-4 backdrop-blur-md">
                  <Building2 className="size-8 text-primary" />
                </div>
                <h3 className="text-2xl font-bold mb-2 tracking-tight">Nexel Core</h3>
                <p className="text-sm font-mono text-primary/80">Unified Technology</p>
              </div>
            </div>

            {}
            {growthModules.map((module, index) => <div key={module.id} data-index={index} className={`${getGridPlacement(index)} relative z-10`}>
                <Card className="h-full bg-card/40 backdrop-blur-xl border-primary/10 shadow-2xl hover:shadow-primary/5 hover:border-primary/40 transition-all duration-500 group">
                  <CardHeader className="pb-4">
                    <div className="bg-primary/10 w-14 h-14 rounded-lg flex items-center justify-center mb-4 group-hover:bg-primary group-hover:text-primary-foreground transition-all duration-500 border border-primary/20">
                      <module.Icon className="size-7 text-primary group-hover:text-primary-foreground transition-colors duration-500" />
                    </div>
                    <CardTitle className="text-xl font-bold tracking-tight text-foreground">{module.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-base text-muted-foreground leading-relaxed">
                      {module.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              </div>)}

          </div>
        </div>

        {}
        <div className="mt-20 text-center">
          <Button asChild size="lg" className="rounded-none border-b-2 border-primary bg-transparent text-foreground hover:bg-primary hover:text-primary-foreground transition-all duration-300 px-10 h-14">
            <Link to="/SolutionsPage">
              Explore Our Solutions
              <ArrowRight className="ml-2 size-5" />
            </Link>
          </Button>
        </div>

      </div>
    </section>;
}